import type { ChangeEvent, FormEvent } from 'react';
import { useState } from 'react';
import useAuth from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import AuthShell from '../components/AuthShell';

function Login() {
    const navigate = useNavigate();
    const { saveTokens } = useAuth();

    const [usernameInput, setUsernameInput] = useState('');
    const [usernameError, setUsernameError] = useState('');
    const [isUsernameValid, setIsUsernameValid] = useState(false);

    const [passwordInput, setPasswordInput] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [passwordError, setPasswordError] = useState('');
    const [isPasswordValid, setIsPasswordValid] = useState(false);

    const [error, setError] = useState('');

    function checkUsername(username: string) {
        setUsernameInput(username);

        if (!username) {
            setIsUsernameValid(false);
            setUsernameError('Please enter a username.');
            return;
        }

        setIsUsernameValid(true);
        setUsernameError('');
        return true;
    }

    function checkPassword(password: string) {
        setPasswordInput(password);

        if (!password) {
            setIsPasswordValid(false);
            setPasswordError('Please enter a password.');
            return;
        }

        setIsPasswordValid(true);
        setPasswordError('');
        return true;
    }

    async function handleLogin(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();

        const hasUsername = usernameInput.trim();
        const hasPassword = passwordInput.trim();

        if (!hasUsername || !hasPassword) {
            setError('Please fill in both fields.');
            return;
        }

        try {
            const response = await fetch('/api/auth/login', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify({ username: usernameInput, password: passwordInput })
            });

            const data = await response.json();

            if (data.success) {
                setError('');
                saveTokens();
                navigate('/');
            } else {
                setError(data.message || 'Login failed');
            }
        } catch (error) {
            setError('Could not connect to the server.');
        }
    }

    return (
        <AuthShell
            title="Sign into your account"
            alternateHref="/register"
            alternateLabel="Don't have an account? Register here"
            feedback={error ? (
                <div className="alert alert-danger" role="alert">
                    {error}
                </div>
            ) : null}
        >
            <form onSubmit={handleLogin} noValidate>
                <div className="form-outline mb-4">
                    <label className="form-label" htmlFor="login-username">
                        Username
                    </label>
                    <input
                        type="text"
                        id="login-username"
                        className={`form-control form-control-lg ${usernameError ? 'is-invalid' : ''}`}
                        placeholder="Username"
                        value={usernameInput}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => checkUsername(event.currentTarget.value)}
                        autoFocus={true}
                    />
                    {usernameError && (
                        <div className="invalid-feedback">
                            {usernameError}
                        </div>
                    )}
                </div>

                <div className="form-outline mb-4">
                    <label className="form-label" htmlFor="login-password">
                        Password
                    </label>

                    <div className="input-group">
                        <input
                            type={showPassword ? 'text' : 'password'}
                            id="login-password"
                            className={`form-control form-control-lg pe-5 ${passwordError ? 'is-invalid' : ''}`}
                            placeholder="Password"
                            value={passwordInput}
                            onChange={(event: ChangeEvent<HTMLInputElement>) => checkPassword(event.currentTarget.value)}
                        />
                        <div
                            className="input-group-text"
                            style={{ cursor: 'pointer' }}
                        >
                            <i
                                className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}
                                onClick={() => setShowPassword(!showPassword)}
                                aria-label={showPassword ? 'Hide password' : 'Show password'}
                                style={{ width: '1.5rem' }}
                            ></i>
                        </div>
                        {passwordError && (
                            <div className="invalid-feedback">
                                {passwordError}
                            </div>
                        )}
                    </div>
                </div>

                <div className="pt-1 mb-4">
                    <button
                        className="btn btn-primary btn-lg btn-block w-100"
                        type="submit"
                        disabled={!isUsernameValid || !isPasswordValid}
                    >
                        Login
                    </button>
                </div>
            </form>
        </AuthShell>
    );
}

export default Login;
